import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { resetPasswordRequestSchema, type ResetInput } from '@munawb/validators';
import { supabase } from '@munawb/supabase';

export default function ResetPasswordPage() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [serverMsg, setServerMsg] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetInput>({ resolver: zodResolver(resetPasswordRequestSchema) });

  const onSubmit = async (data: ResetInput) => {
    setServerMsg(null);
    try {
      const { data: result, error } = await supabase.functions.invoke('auth-reset-password', {
        body: { action: 'request', email: data.email },
      });

      if (error) throw error;
      if (result?.error) throw new Error(result.error);

      setStatus('success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'حدث خطأ غير متوقع';
      setServerMsg(msg);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950 p-4">
      <div className="glass-panel w-full max-w-md rounded-3xl p-8 shadow-2xl">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shadow-md">
            <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-white">نسيت كلمة المرور؟</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 font-bold mt-1">
            أدخل بريدك لإرسال رابط إعادة التعيين
          </p>
        </div>

        {/* Success State */}
        {status === 'success' ? (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-base font-black text-slate-800 dark:text-white">تم الإرسال</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-bold mt-1">
                إذا كان البريد مسجلاً ستصلك رسالة خلال دقائق.
                <br />
                تحقق من صندوق الوارد أو البريد المزعج.
              </p>
            </div>
            <a
              href="/"
              className="btn-primary inline-block px-6 py-2.5 text-sm rounded-xl"
            >
              العودة لتسجيل الدخول
            </a>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>

            <div>
              <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
                البريد الإلكتروني
              </label>
              <input
                type="email"
                autoComplete="email"
                placeholder="example@hospital.com"
                className="input-field"
                dir="ltr"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-red-500 text-xs font-bold mt-1">{errors.email.message}</p>
              )}
            </div>

            {status === 'error' && serverMsg && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 text-center">
                <p className="text-red-600 dark:text-red-400 text-sm font-bold">{serverMsg}</p>
              </div>
            )}

            <button type="submit" className="btn-primary w-full py-3 text-base" disabled={isSubmitting}>
              {isSubmitting ? (
                <><span className="spinner border-white/30 border-t-white" /> جارٍ الإرسال...</>
              ) : (
                'إرسال رابط إعادة التعيين'
              )}
            </button>

            <div className="text-center">
              <a href="/" className="text-sm font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:underline">
                ← العودة لتسجيل الدخول
              </a>
            </div>
          </form>
        )}

      </div>
    </div>
  );
}
